/***
 *
 *   DATE PICKER
 *   Date picker input
 *
 *   DOCS
 *   https://docs.useMapMetrics Atlas.app/MapMetrics Atlas-web/components/form
 *   https://ui.shadcn.com/docs/components/date-picker
 *
 *   PROPS
 *   aria-describedby: id of the element that describes the input in ...props (string, optional)
 *   aria-invalid: determines if the input is invalid in ...props (boolean, required)
 *   className: custom styling (SCSS or tailwind style, optional)
 *   defaultValue: initial value (string, optional)
 *   placeholder: placeholder value (string, optional)
 *   onChange: callback (function, required)
 *
 **********/

import { forwardRef, useState } from "react";
import { format } from "date-fns";
import {
  Calendar,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
  useTranslation,
} from "components/lib";

const DateInput = forwardRef(
  ({ className, defaultValue, placeholder, onChange, ...props }, ref) => {
    const [date, setDate] = useState(
      defaultValue ? new Date(defaultValue) : null
    );
    const { t } = useTranslation();

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            text={
              date
                ? format(date, "PPP")
                : placeholder || t("global.form.date.placeholder")
            }
            icon="calendar"
            variant="outline"
            className={cn(
              "w-[280px] justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          />
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0">
          <Calendar
            initialFocus
            mode="single"
            selected={date}
            onSelect={(value) => {
              // create pure date
              const year = value.getFullYear();
              const month = String(value.getMonth() + 1).padStart(2, "0"); // months are zero-indexed
              const day = String(value.getDate()).padStart(2, "0");

              // format the date as 'YYYY-MM-DD' without timezones
              const selectedDate = `${year}-${month}-${day}`;

              setDate(value); // update state with the selected date object
              onChange({
                target: { name: props.name, value: selectedDate },
                type: "change",
              });
            }}
          />
        </PopoverContent>
      </Popover>
    );
  }
);

DateInput.displayName = "DateInput";

const DateRangeInput = forwardRef(
  ({ className, defaultValue, placeholder, onChange, ...props }, ref) => {
    // Extract the start and end dates from defaultValue if provided
    const initialStartDate = defaultValue?.start
      ? new Date(defaultValue.start)
      : null;
    const initialEndDate = defaultValue?.end
      ? new Date(defaultValue.end)
      : null;

    // State for start and end dates
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(initialEndDate);

    const { t } = useTranslation();

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            text={
              startDate && endDate
                ? `${format(startDate, "PPP")} - ${format(endDate, "PPP")}` // Show formatted range
                : placeholder || t("global.form.date.rangePlaceholder")
            }
            // icon="calendar"
            variant="outline"
            className={cn(
              "text-[14px] text-primary justify-start bg-white border-0 dark:bg-slate-800 text-left font-normal",
              (!startDate || !endDate) && "text-muted-foreground"
            )}
          />
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0">
          <Calendar
            initialFocus
            mode="range" // Set calendar mode to range
            selected={{ from: startDate, to: endDate }} // Pass selected range
            onSelect={(range) => {
              const { from, to } = range;

              // Convert selected dates to 'YYYY-MM-DD' format
              const formattedStart = from ? format(from, "yyyy-MM-dd") : null;
              const formattedEnd = to ? format(to, "yyyy-MM-dd") : null;

              // Update state
              setStartDate(from);
              setEndDate(to);

              // Trigger onChange with range values
              onChange({
                target: {
                  name: props.name,
                  value: { start: formattedStart, end: formattedEnd },
                },
                type: "change",
              });
            }}
          />
        </PopoverContent>
      </Popover>
    );
  }
);

DateRangeInput.displayName = "DateRangeInput";
export { DateInput, DateRangeInput };
